import React, { useEffect, useRef } from "react";

import { useUserEvents } from "hooks";

import Icon_MidstreamAllFacilities from "components/icons/Icon_MidstreamAllFacilities";
import { NetworkFraction } from "components/map/hooks/useGetFacilityNetworkFraction";
import {
  midstreamNavigatorActions,
  useMidstreamNavigatorContext
} from "components/midstream/midstream-navigator/context/MidstreamNavigatorContext";

import { FilterKey, useFilterButtonContext } from "../context/FilterButtonContext";
import { BaseMidstreamFilterButton, FilterConfig } from "../filters/BaseFilterButton";
import { useContributingWellsFromAllFacilitiesFilter } from "../hooks/useContributingWellsFromAllFacilitiesFilter";
import { useFilterUndo } from "../hooks/useFilterUndo";

export const ContributingWellsFromAllFacilitiesButton: React.FC = () => {
  const [state, dispatch] = useMidstreamNavigatorContext();
  const {
    wellFilterCount,
    getIsDisabled,
    clearOtherFacilityFilters,
    withUserAction,
    useExternalFilterMonitoring,
    MonitoringStrategies
  } = useFilterButtonContext();

  const {
    filterToContributingWellsFromAllFacilities,
    removeContributingWellsFromAllFacilitiesFilter,
    facilitiesInGroup,
    networkFraction
  } = useContributingWellsFromAllFacilitiesFilter();

  const { undoFilters } = useFilterUndo();
  const { sendMidstreamNavigatorButtonToggledEvent } = useUserEvents();

  const pendingEventDataRef = useRef<{
    facilityList: string[];
    previousCount: number;
    networkFractions: NetworkFraction[];
  } | null>(null);

  const config: FilterConfig = {
    icon: <Icon_MidstreamAllFacilities />,
    tooltip: "Filter To Contributing Wells From All Facilities",
    testId: `filter-button-${FilterKey.ContributingWellsFromAllFacilities}`
  };

  const isActive =
    state.activeFilters?.includes(FilterKey.ContributingWellsFromAllFacilities) ?? false;
  const isDisabled = getIsDisabled(
    FilterKey.ContributingWellsFromAllFacilities,
    isActive
  );
  const disabledReason = isDisabled ? "Select a facility to filter" : null;

  useExternalFilterMonitoring(
    FilterKey.ContributingWellsFromAllFacilities,
    isActive,
    MonitoringStrategies.contributingWells
  );

  // Auto-cleanup when button becomes disabled while active
  useEffect(() => {
    if (isDisabled && isActive) {
      dispatch(
        midstreamNavigatorActions.removeFilter(
          FilterKey.ContributingWellsFromAllFacilities
        )
      );
      removeContributingWellsFromAllFacilitiesFilter();
    }
  }, [isDisabled, isActive, dispatch, undoFilters]);

  useEffect(() => {
    if (pendingEventDataRef.current && wellFilterCount !== undefined && isActive) {
      const {
        facilityList,
        previousCount,
        networkFractions: storedFractions
      } = pendingEventDataRef.current;
      const shouldSendEvent =
        (wellFilterCount !== undefined && wellFilterCount !== previousCount) ||
        networkFraction === null ||
        networkFraction?.length === 0;

      if (shouldSendEvent) {
        const contributingWells = (networkFraction || storedFractions || []).map(
          (fraction) => fraction.wellId
        );

        sendMidstreamNavigatorButtonToggledEvent({
          filterKey: FilterKey.ContributingWellsFromAllFacilities,
          facilityList: facilityList,
          wellFilterCount: wellFilterCount,
          contributingWells: contributingWells
        });

        pendingEventDataRef.current = null;
      }
    }
  }, [
    wellFilterCount,
    networkFraction,
    isActive,
    sendMidstreamNavigatorButtonToggledEvent
  ]);

  const handleToggle = () => {
    withUserAction(async () => {
      if (!isActive) {
        clearOtherFacilityFilters(FilterKey.ContributingWellsFromAllFacilities);
        dispatch(
          midstreamNavigatorActions.addFilter(
            FilterKey.ContributingWellsFromAllFacilities
          )
        );
        const facilityIds: string[] = [];
        for (const title of Object.keys(facilitiesInGroup)) {
          const group = facilitiesInGroup[title];
          for (const id of Object.keys(group.uwis)) {
            facilityIds.push(id);
          }
        }
        // Store facility list before filtering for analytics
        pendingEventDataRef.current = {
          facilityList: facilityIds,
          previousCount: wellFilterCount || 0,
          networkFractions: networkFraction || []
        };

        await filterToContributingWellsFromAllFacilities();
      } else {
        dispatch(
          midstreamNavigatorActions.removeFilter(
            FilterKey.ContributingWellsFromAllFacilities
          )
        );
        removeContributingWellsFromAllFacilitiesFilter(true);
      }
    });
  };

  return (
    <BaseMidstreamFilterButton
      config={config}
      isActive={isActive}
      isDisabled={isDisabled}
      onToggle={handleToggle}
      disabledReason={disabledReason}
    />
  );
};
